// appraisals 컬렉션 헬퍼.
// geek1 production 데이터 구조 그대로 (docs/05-geek1-schema.md §1 참조).

import dayjs from 'dayjs';
import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Timestamp
} from 'firebase/firestore';

import { db } from 'firebase/config';
import { deleteAllAppraisalFiles, deleteFile, uploadAppraisalFile } from 'firebase/storage-helpers';

// sub-array row 모양
export interface FeeRow {
  index: string;
  type: string;
  date: string;
  amount: number;
  remarks?: string;
}

export interface ProgressRow {
  index: string;
  type: string;
  start_date: string;
  end_date: string;
  remarks?: string;
  attachment_file?: string;
  file_id?: string;
}

export interface TrialProgressRow {
  index: string;
  type: string;
  date: string;
  remarks?: string;
}

export interface MaterialRow {
  index: string;
  parties: string;
  date: string;
  remarks?: string;
  attachment_file?: string;
  file_id?: string;
}

export interface DepositRow {
  index: string;
  date: string;
  amount: number;
  type?: string;
  remarks?: string;
}

export interface RequestFileRow {
  type: string; // rowKey — 고정 6 값 중 하나
  attachment_file: string;
  file_id: string;
}

export interface FileInfo {
  file_id: string;
  url?: string; // storage path (geek1 호환). download 시 getDownloadURL 로 변환
  del_yn?: '1';
}

// form values 가 들고 다니는 모양. 저장 시점에 file 은 업로드 → url 로 교체.
export interface FileInfoForm extends FileInfo {
  file?: File;
}

// 저장 직전 호출 — 신규 파일은 업로드, del_yn 인 것은 삭제, 그 외는 그대로.
// 반환값:
//   fileInfo — Appraisal.file_info 에 넣을 결과 (Blob 제거)
//   newlyUploadedPaths — 이번에 신규 업로드한 Storage path 목록 (Firestore 저장 실패 시 rollback 용)
//
// ⚠️ Storage delete 는 영구 — rollback 불가. del_yn 처리 순서를 Firestore 저장 이후로 미뤄도
//    Storage delete 실패 시 orphan 위험은 남는다(구조적). 따라서 실패는 삼키지 않고 경고로 가시화하고,
//    드물게 남는 고아는 백스톱인 scripts/cleanup-orphans.mjs(주기 실행)로 회수한다.
export async function processFileInfoForSave(
  fileInfoForm: FileInfoForm[],
  appraisalDocId: string
): Promise<{ fileInfo: FileInfo[]; newlyUploadedPaths: string[] }> {
  const result: FileInfo[] = [];
  const newlyUploadedPaths: string[] = [];

  for (const fi of fileInfoForm) {
    if (fi.del_yn === '1') {
      if (fi.url) {
        try {
          await deleteFile(fi.url);
        } catch (e) {
          // 이미 없으면 정상. 그 외 실패는 참조만 빠지고 실물이 남아 고아가 되므로 경고로 가시화.
          console.warn(`[첨부삭제 실패] ${fi.url} — 고아 가능(cleanup-orphans 로 회수)`, e);
        }
      }
      continue;
    }
    if (fi.file && !fi.url) {
      const { storagePath } = await uploadAppraisalFile(appraisalDocId, fi.file);
      newlyUploadedPaths.push(storagePath);
      result.push({ file_id: fi.file_id, url: storagePath });
      continue;
    }
    if (fi.url) {
      result.push({ file_id: fi.file_id, url: fi.url });
    }
  }

  return { fileInfo: result, newlyUploadedPaths };
}

// Firestore 저장이 실패한 경우, 이번에 새로 업로드된 Storage 파일을 정리.
export async function rollbackUploadedFiles(paths: string[]): Promise<void> {
  for (const path of paths) {
    try {
      await deleteFile(path);
    } catch {
      // 실패 무시 — 이미 없거나 권한 없거나. 다음 cleanup job 에서 잡힘.
    }
  }
}

// 화면이 다루는 필드. 점진 확장.
export interface Appraisal {
  id: string;
  year: number;
  case_number_type: string;
  case_number: string;
  case_number_remark?: string;
  case_fullnumber: string;
  title: string;
  submission_date?: string;
  asign_date?: string;
  status?: string;
  appraisal_category?: string;
  court?: string;
  judge_panel?: string;
  judge_panel_telno?: string;
  judge_panel_faxno?: string;
  judge_panel_email?: string;
  object_address?: string;
  object_remarks?: string;
  plaintiff_name?: string;
  plaintiff_telno?: string;
  plaintiff_rep_name?: string;
  plaintiff_rep_telno?: string;
  defendant_name?: string;
  defendant_telno?: string;
  defendant_rep_name?: string;
  defendant_rep_telno?: string;
  plaintiff_fee?: FeeRow[];
  defendant_fee?: FeeRow[];
  plaintiff_deposits?: DepositRow[];
  defendant_deposits?: DepositRow[];
  appraisal_progress?: ProgressRow[];
  trial_progress?: TrialProgressRow[];
  appraisal_materials?: MaterialRow[];
  request_files?: RequestFileRow[];
  file_info?: FileInfo[];
  total_plaintiff_fee?: number;
  total_defendant_fee?: number;
  total_fee?: number;
  total_plaintiff_remainders?: number;
  total_defendant_remainders?: number;
  total_remainders?: number;
  total_remainders_yn?: boolean;
  // 2차 개선 신규 필드 (09 §1.1)
  is_assigned?: boolean;
  kukyang_case_number?: string;
  kukyang_staff_uid?: string;
  assignment_type?: string;
  assignment_date?: string;
  appraisal_submission_date?: string;
  // 메타
  create_userid?: string;
  create_timestamp?: Timestamp;
  modify_userid?: string;
  modify_timestamp?: Timestamp;
}

const COLLECTION = 'appraisals';

// 전체 188건 실시간 구독. 클라이언트 사이드 필터링 정책 (09 §6.1).
export function subscribeToAppraisals(onChange: (rows: Appraisal[]) => void): () => void {
  const q = query(collection(db, COLLECTION));
  return onSnapshot(q, (snapshot) => {
    const rows: Appraisal[] = snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Appraisal, 'id'>) }));
    onChange(rows);
  });
}

// 전체 사건 수 — 서버 집계 1회(문서 전체를 내려받지 않음). 30주년 모달 표시용.
export async function countAppraisals(): Promise<number> {
  const snap = await getCountFromServer(collection(db, COLLECTION));
  return snap.data().count;
}

// 단건 실시간 구독.
export function subscribeToAppraisal(
  id: string,
  onChange: (row: Appraisal | null) => void
): () => void {
  return onSnapshot(doc(db, COLLECTION, id), (snapshot) => {
    if (!snapshot.exists()) {
      onChange(null);
      return;
    }
    onChange({ id: snapshot.id, ...(snapshot.data() as Omit<Appraisal, 'id'>) });
  });
}

// geek1 의 generateId 패턴 — YYYYMMDDhhmmss-NNNN (클라이언트 시각 + random 4자리)
export function generateAppraisalId(): string {
  const d = new Date();
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  const ts = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  const rand = pad(Math.floor(Math.random() * 10000), 4);
  return `${ts}-${rand}`;
}

// 신규 사건의 고정 6 row request_files (geek1 firebase.js:197-203)
export const REQUEST_FILE_TYPES = [
  '감정신청서 원고 1',
  '감정신청서 원고 2',
  '감정신청서 피고 1',
  '감정신청서 피고 2',
  '예상 감정자료 산출근거',
  '감정인 지정 결정문'
] as const;

export function emptyRequestFiles(): RequestFileRow[] {
  return REQUEST_FILE_TYPES.map((type) => ({ type, attachment_file: '', file_id: '' }));
}

// 저장 — 신규/편집 모두 setDoc 사용 (geek1 firebase.js:281-298 와 동일 정책).
// 호출자가 case_fullnumber, total_*, modify_* 등은 적절히 채워서 보내야 함.
// uid 가 있어야 create/modify_userid 채움.
export async function saveAppraisal(item: Appraisal, uid: string, isNew: boolean): Promise<void> {
  const now = serverTimestamp();
  const payload: Record<string, unknown> = {
    ...item,
    modify_userid: uid,
    modify_timestamp: now
  };
  if (isNew) {
    payload.create_userid = uid;
    payload.create_timestamp = now;
  }
  await setDoc(doc(db, COLLECTION, item.id), payload, { merge: false });
}

// 사건 삭제 — Firestore doc 삭제 + 그 사건 폴더의 Storage 첨부 동반 정리.
// 순서: 권위 기록인 doc 을 먼저 지운 뒤 Storage 폴더를 정리한다(doc 이 사라지므로
// 첨부 정리가 일부 실패해도 '깨진 참조'는 생기지 않고, 잔존분은 고아로만 남아 cleanup-orphans 로 회수).
export async function deleteAppraisal(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
  try {
    const r = await deleteAllAppraisalFiles(id);
    if (r.failed > 0) {
      console.warn(`[deleteAppraisal] ${id}: 첨부 ${r.failed}개 삭제 실패 — 고아 가능(cleanup-orphans 로 회수)`);
    }
  } catch (e) {
    console.warn(`[deleteAppraisal] ${id}: 첨부 정리 실패 — 고아 가능(cleanup-orphans 로 회수)`, e);
  }
}

// 감정인 선정 — 부분 update. 09 §4.1 (목록 인라인 액션) + §4.2 (detail 액션) 공용.
//
// 동작 (09 §1.4.3 + §6.4):
//   - is_assigned = true
//   - kukyang_case_number — 처음이면 채번, 이미 있으면 유지 (회수 안 함)
//     단 **옛 사건 (asign_date 있고 kukyang_case_number 없음)** 은 채번 안 함 — 09 §1.1 "빈칸 허용 (옛 사건)"
//   - asign_date — 처음이면 today, 이미 있으면 유지 (geek1 동작)
//
// 반환: 최종 채번/일자 (caller 가 알림/이동에 활용). 옛 사건의 채번은 빈 string.
//
// 채번 연도는 **선정 시점 (today)** — 09 §6.4 ("시스템 도입 연도 2026 의 첫 사건 = 26-01").
// 사건번호의 연도가 아님 (옛 2024 사건을 2026 에 선정해도 26-NN).
export async function assignAppraiser(
  id: string,
  uid: string
): Promise<{ kukyangCaseNumber: string; asignDate: string }> {
  const ref = doc(db, COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('사건을 찾을 수 없습니다');
  const cur = snap.data() as Partial<Appraisal>;

  // 옛 사건 = geek1 시절 등록되어 한 번도 채번 받은 적 없는 사건.
  // production 188건 모두 이 패턴 (08 §점검3 — asign_date 100%, kukyang_case_number 0%).
  const isLegacyCase = !cur.kukyang_case_number && !!cur.asign_date;
  const kukyangCaseNumber = isLegacyCase
    ? undefined
    : cur.kukyang_case_number ?? (await nextKukyangCaseNumber(dayjs().year()));
  const asignDate = cur.asign_date ?? dayjs().format('YYYY-MM-DD');

  const update: Record<string, unknown> = {
    is_assigned: true,
    asign_date: asignDate,
    modify_userid: uid,
    modify_timestamp: serverTimestamp()
  };
  if (kukyangCaseNumber !== undefined) {
    update.kukyang_case_number = kukyangCaseNumber;
  }
  await updateDoc(ref, update);

  return { kukyangCaseNumber: kukyangCaseNumber ?? '', asignDate };
}

// 다음 국양 감정번호 — `YY-NN` (연도 리셋, 09 §6.4).
// 같은 연도 안에서 max NN + 1. 채번 회수 안 함 (한 번 받으면 영구).
//
// Firestore single-field range query 로 같은 연도 prefix 만 fetch (자동 인덱스).
// 해당 연도 첫 채번이면 결과 0건 → maxNN=0 → "YY-01" 반환.
export async function nextKukyangCaseNumber(year: number): Promise<string> {
  const yy = String(year).slice(-2).padStart(2, '0');
  const prefix = `${yy}-`;
  const q = query(
    collection(db, COLLECTION),
    where('kukyang_case_number', '>=', prefix),
    where('kukyang_case_number', '<=', prefix + '￿')
  );
  const snapshot = await getDocs(q);
  let maxNN = 0;
  snapshot.forEach((d) => {
    const num = (d.data().kukyang_case_number ?? '') as string;
    const nn = parseInt(num.slice(prefix.length), 10);
    if (!Number.isNaN(nn) && nn > maxNN) maxNN = nn;
  });
  return `${prefix}${String(maxNN + 1).padStart(2, '0')}`;
}
